import React, { useEffect, useMemo, useState } from "react";
import Sidebar from "../../components/Sidebar";
import Navbar from "../../components/Navbar";
import toast from "react-hot-toast";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import axiosInstance from "../../../axiosInstance";

const MySwal = withReactContent(Swal);

const TAX_TYPE = ["GST", "VAT", "SALES_TAX", "NONE"];

export default function AdminTaxRulesSinglePage() {
  const [loading, setLoading] = useState(true);

  // dropdown data
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [categories, setCategories] = useState([]); // ✅ category dropdown

  // list
  const [rules, setRules] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");

  // add form
  const [addLoading, setAddLoading] = useState(false);
  const [addForm, setAddForm] = useState({
    countryId: "",
    stateId: "",
    cityId: "",
    areaId: "",
    categoryId: "", // ✅ optional
    taxType: "NONE",
    rate: "",
    isInclusive: false,
    effectiveFrom: "",
    effectiveTo: "",
    status: true,
  });

  // edit modal
  const [editOpen, setEditOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editForm, setEditForm] = useState({
    _id: "",
    countryId: "",
    stateId: "",
    cityId: "",
    areaId: "",
    categoryId: "",
    taxType: "NONE",
    rate: "",
    isInclusive: false,
    effectiveFrom: "",
    effectiveTo: "",
    status: true,
  });

  // ------------------- helpers -------------------
  const toISODateInput = (d) => {
    if (!d) return "";
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return "";
    const yyyy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const dd = String(dt.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const buildListQuery = () => {
    const params = new URLSearchParams();
    params.set("page", String(currentPage));
    params.set("limit", String(limit));
    params.set("search", searchTerm || "");
    return params.toString();
  };

  const normalizePayload = (f) => {
    return {
      countryId: f.countryId,
      stateId: f.stateId || null,
      cityId: f.cityId || null,
      areaId: f.areaId || null,
      categoryId: f.categoryId || null, // ✅ optional
      taxType: f.taxType || "NONE",
      rate: Number(f.rate),
      isInclusive: !!f.isInclusive,
      effectiveFrom: f.effectiveFrom ? new Date(f.effectiveFrom).toISOString() : null,
      effectiveTo: f.effectiveTo ? new Date(f.effectiveTo).toISOString() : null,
      status: !!f.status,
    };
  };

  // ------------------- dropdown loaders -------------------
  const loadCountries = async () => {
    const res = await axiosInstance.get("/admin/countries?page=1&limit=500&search=");
    setCountries(res.data.countries || res.data.Countries || []);
  };

  const loadStates = async (countryId) => {
    if (!countryId) return setStates([]);
    const res = await axiosInstance.get(`/admin/states?page=1&limit=500&search=&countryId=${countryId}`);
    setStates(res.data.states || res.data.States || []);
  };

  const loadCities = async (countryId, stateId) => {
    if (!countryId || !stateId) return setCities([]);
    const res = await axiosInstance.get(`/admin/cities?page=1&limit=500&search=&countryId=${countryId}&stateId=${stateId}`);
    setCities(res.data.cities || res.data.Cities || []);
  };

  // ✅ If you have categories API, set it here:
  // Change endpoint according to your backend:
  const loadCategories = async () => {
    try {
      const res = await axiosInstance.get("/admin/all-category-fillter?page=1&limit=10&search=");
      setCategories(res.data.Category || res.data.Category || []);
    } catch {
      setCategories([]);
    }
  };

  // ------------------- list loader -------------------
  const fetchRules = async () => {
    try {
      setLoading(true);
      const q = buildListQuery();
      const res = await axiosInstance.get(`/admin/tax-rules?${q}`);

      const list = res.data.rules || [];
      setRules(list);

      setCurrentPage(res.data.currentPage || currentPage);
      setTotalPages(res.data.totalPages || 1);
    } catch (e) {
      console.error(e);
      toast.error("Error fetching tax rules");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCountries().catch(() => toast.error("Error loading countries"));
    loadCategories();
  }, []);

  useEffect(() => {
    fetchRules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, limit]);

  const onSearch = () => {
    setCurrentPage(1);
    fetchRules();
  };

  // ------------------- cascade for add -------------------
  useEffect(() => {
    loadStates(addForm.countryId).catch(() => {});
    setAddForm((p) => ({ ...p, stateId: "", cityId: "", areaId: "" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addForm.countryId]);

  useEffect(() => {
    loadCities(addForm.countryId, addForm.stateId).catch(() => {});
    setAddForm((p) => ({ ...p, cityId: "", areaId: "" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addForm.stateId]);

  const selectedCityForAdd = useMemo(() => cities.find((c) => c._id === addForm.cityId), [cities, addForm.cityId]);
  const addAreasOptions = useMemo(() => (selectedCityForAdd?.areas || []).filter((a) => a.status !== false), [selectedCityForAdd]);

  const handleAddChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox") return setAddForm((p) => ({ ...p, [name]: checked }));
    setAddForm((p) => ({ ...p, [name]: value }));
  };

  const addTaxRule = async () => {
    if (!addForm.countryId) return toast.error("Select country");
    if (addForm.rate === "" || Number(addForm.rate) < 0) return toast.error("Enter valid rate");

    try {
      setAddLoading(true);
      await axiosInstance.post("/admin/tax-rule", normalizePayload(addForm));

      toast.success("Tax rule added!");
      setAddForm({
        countryId: "",
        stateId: "",
        cityId: "",
        areaId: "",
        categoryId: "",
        taxType: "NONE",
        rate: "",
        isInclusive: false,
        effectiveFrom: "",
        effectiveTo: "",
        status: true,
      });

      setCurrentPage(1);
      fetchRules();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Error adding rule");
    } finally {
      setAddLoading(false);
    }
  };

  // ------------------- EDIT -------------------
  const openEdit = async (row) => {
    try {
      const res = await axiosInstance.get(`/admin/tax-rule/${row._id}`);
      const r = res.data.rule;

      const countryId = r.countryId?._id || r.countryId || "";
      const stateId = r.stateId?._id || r.stateId || "";
      const cityId = r.cityId?._id || r.cityId || "";

      await loadStates(countryId);
      await loadCities(countryId, stateId);

      setEditForm({
        _id: r._id,
        countryId,
        stateId: stateId || "",
        cityId: cityId || "",
        areaId: r.areaId || "",
        categoryId: r.categoryId?._id || r.categoryId || "",
        taxType: r.taxType || "NONE",
        rate: r.rate ?? "",
        isInclusive: r.isInclusive === true,
        effectiveFrom: toISODateInput(r.effectiveFrom),
        effectiveTo: toISODateInput(r.effectiveTo),
        status: r.status === true,
      });

      setEditOpen(true);
    } catch (e) {
      toast.error("Error opening rule");
    }
  };

  const handleEditChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox") return setEditForm((p) => ({ ...p, [name]: checked }));

    if (name === "countryId") {
      setEditForm((p) => ({ ...p, countryId: value, stateId: "", cityId: "", areaId: "" }));
      loadStates(value).catch(() => {});
      setCities([]);
      return;
    }
    if (name === "stateId") {
      setEditForm((p) => ({ ...p, stateId: value, cityId: "", areaId: "" }));
      loadCities(editForm.countryId, value).catch(() => {});
      return;
    }
    if (name === "cityId") {
      setEditForm((p) => ({ ...p, cityId: value, areaId: "" }));
      return;
    }

    setEditForm((p) => ({ ...p, [name]: value }));
  };

  const selectedCityForEdit = useMemo(() => cities.find((c) => c._id === editForm.cityId), [cities, editForm.cityId]);
  const editAreasOptions = useMemo(() => (selectedCityForEdit?.areas || []).filter((a) => a.status !== false), [selectedCityForEdit]);

  const updateTaxRule = async () => {
    if (!editForm.countryId) return toast.error("Select country");
    if (editForm.rate === "" || Number(editForm.rate) < 0) return toast.error("Enter valid rate");

    try {
      setEditLoading(true);
      await axiosInstance.put(`/admin/tax-rule/${editForm._id}`, normalizePayload(editForm));
      toast.success("Tax rule updated!");
      setEditOpen(false);
      fetchRules();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Error updating rule");
    } finally {
      setEditLoading(false);
    }
  };

  // ------------------- DELETE -------------------
  const deleteTaxRule = (id) => {
    MySwal.fire({
      title: "Are you sure?",
      text: "You will not be able to recover this tax rule!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
      reverseButtons: true,
    }).then(async (result) => {
      if (!result.isConfirmed) return;

      try {
        await axiosInstance.delete(`/admin/tax-rule/${id}`);
        toast.success("Tax rule deleted!");
        fetchRules();
      } catch (e) {
        toast.error(e?.response?.data?.message || "Error deleting rule");
      }
    });
  };

  return (
    <>
      <Sidebar />
      <main className="content">
        <Navbar />

        <div className="py-4">
          <h1 className="h4">Tax Rules (Location + Category)</h1>
        </div>

        {/* ADD FORM */}
        <div className="card mb-4">
          <div className="card-body">
            <div className="row g-3 align-items-end">
              <div className="col-md-3">
                <label className="form-label">Country</label>
                <select className="form-control" name="countryId" value={addForm.countryId} onChange={handleAddChange}>
                  <option value="">Select</option>
                  {countries.map((c) => (
                    <option key={c._id} value={c._id}>
                      {c.name} ({c.iso2 || "-"})
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-3">
                <label className="form-label">State (optional)</label>
                <select className="form-control" name="stateId" value={addForm.stateId} onChange={handleAddChange} disabled={!addForm.countryId}>
                  <option value="">All</option>
                  {states.map((s) => (
                    <option key={s._id} value={s._id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-3">
                <label className="form-label">City (optional)</label>
                <select className="form-control" name="cityId" value={addForm.cityId} onChange={handleAddChange} disabled={!addForm.stateId}>
                  <option value="">All</option>
                  {cities.map((c) => (
                    <option key={c._id} value={c._id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-3">
                <label className="form-label">Area (optional)</label>
                <select className="form-control" name="areaId" value={addForm.areaId} onChange={handleAddChange} disabled={!addForm.cityId}>
                  <option value="">All</option>
                  {addAreasOptions.map((a) => (
                    <option key={a._id} value={a._id}>
                      {a.name} {a.pincode ? `(${a.pincode})` : ""}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-3">
                <label className="form-label">Category (optional)</label>
                <select className="form-control" name="categoryId" value={addForm.categoryId} onChange={handleAddChange}>
                  <option value="">All Categories</option>
                  {categories.map((cat) => (
                    <option key={cat._id} value={cat._id}>
                      {cat.title}
                    </option>
                  ))}
                </select>
               </div>

              <div className="col-md-2">
                <label className="form-label">Tax Type</label>
                <select className="form-control" name="taxType" value={addForm.taxType} onChange={handleAddChange}>
                  {TAX_TYPE.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-2">
                <label className="form-label">Rate (%)</label>
                <input className="form-control" name="rate" value={addForm.rate} onChange={handleAddChange} placeholder="18" />
              </div>

              <div className="col-md-2">
                <label className="form-label">Inclusive</label>
                <div className="form-check">
                  <input className="form-check-input" type="checkbox" name="isInclusive" checked={addForm.isInclusive} onChange={handleAddChange} />
                  <label className="form-check-label">Price includes tax</label>
                </div>
              </div>

              <div className="col-md-2">
                <label className="form-label">Effective From</label>
                <input className="form-control" type="date" name="effectiveFrom" value={addForm.effectiveFrom} onChange={handleAddChange} />
              </div>

              <div className="col-md-2">
                <label className="form-label">Effective To</label>
                <input className="form-control" type="date" name="effectiveTo" value={addForm.effectiveTo} onChange={handleAddChange} />
              </div>

              <div className="col-md-2">
                <label className="form-label">Status</label>
                <div className="d-flex gap-3">
                  <div className="form-check">
                    <input className="form-check-input" type="radio" checked={addForm.status === true} onChange={() => setAddForm((p) => ({ ...p, status: true }))} />
                    <label className="form-check-label text-success">Active</label>
                  </div>
                  <div className="form-check">
                    <input className="form-check-input" type="radio" checked={addForm.status === false} onChange={() => setAddForm((p) => ({ ...p, status: false }))} />
                    <label className="form-check-label text-danger">Inactive</label>
                  </div>
                </div>
              </div>

              <div className="col-12">
                <button className="btn btn-primary" type="button" onClick={addTaxRule} disabled={addLoading}>
                  {addLoading ? "Adding..." : "Add Tax Rule"}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* LIST */}
        <div className="card">
          <div className="table-responsive py-4">
            <div className="datatable-top px-3">
              <div className="datatable-dropdown">
                <label>
                  <select
                    className="datatable-selector"
                    value={limit}
                    onChange={(e) => {
                      setLimit(parseInt(e.target.value, 10));
                      setCurrentPage(1);
                    }}
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={15}>15</option>
                    <option value={20}>20</option>
                    <option value={25}>25</option>
                  </select>{" "}
                  entries per page
                </label>
              </div>

              <div className="d-flex align-items-center gap-2">
                <div className="datatable-search">
                  <input
                    className="datatable-input"
                    type="text"
                    placeholder="Search taxType / areaSlug..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                  />
                </div>
                <button className="btn btn-primary" onClick={onSearch}>
                  Search
                </button>
              </div>
            </div>

            {loading ? (
              <div className="p-4">Loading...</div>
            ) : (
              <>
                <table className="table table-flush">
                  <thead>
                    <tr>
                      <th>Location</th>
                      <th>Category</th>
                      <th>Tax Type</th>
                      <th>Rate</th>
                      <th>Inclusive</th>
                      <th>Effective</th>
                      <th>Status</th>
                      <th style={{ width: 220 }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rules.map((r) => {
                      const countryName = r.countryId?.name || "-";
                      const stateName = r.stateId?.name || "All";
                      const cityName = r.cityId?.name || "All";
                      const areaName = r.areaSlug || (r.areaId ? "Area" : "All");
                      const categoryName = r.categoryId?.name || "All";

                      return (
                        <tr key={r._id}>
                          <td>
                            {countryName} / {stateName} / {cityName} / {areaName}
                          </td>
                          <td>{categoryName}</td>
                          <td>{r.taxType}</td>
                          <td>{r.rate}%</td>
                          <td>{r.isInclusive ? "Yes" : "No"}</td>
                          <td>
                            {r.effectiveFrom ? toISODateInput(r.effectiveFrom) : "-"} →{" "}
                            {r.effectiveTo ? toISODateInput(r.effectiveTo) : "-"}
                          </td>
                          <td>
                            <span className={`badge ${r.status ? "bg-success" : "bg-danger"}`}>{r.status ? "Active" : "Inactive"}</span>
                          </td>
                          <td>
                            <button className="btn btn-primary me-2" onClick={() => openEdit(r)}>
                              Edit
                            </button>
                            <button className="btn btn-danger" onClick={() => deleteTaxRule(r._id)}>
                              Delete
                            </button>
                          </td>
                        </tr>
                      );
                    })}

                    {!rules.length && (
                      <tr>
                        <td colSpan={8} className="text-center py-4">
                          No tax rules found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>

                <div className="datatable-bottom px-3">
                  <nav className="datatable-pagination">
                    <ul className="datatable-pagination-list">
                      <li className={`datatable-pagination-list-item ${currentPage === 1 ? "datatable-hidden" : "datatable-active"}`}>
                        <button className="datatable-pagination-list-item-link" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}>
                          ‹
                        </button>
                      </li>

                      <li>
                        <div className="datatable-info py-1 px-3">
                          Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
                        </div>
                      </li>

                      <li className={`datatable-pagination-list-item ${currentPage === totalPages ? "datatable-hidden" : "datatable-active"}`}>
                        <button className="datatable-pagination-list-item-link" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
                          ›
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              </>
            )}
          </div>
        </div>

        {/* EDIT MODAL */}
        {editOpen && (
          <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ background: "rgba(0,0,0,.45)" }}>
            <div className="modal-dialog modal-xl" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Edit Tax Rule</h5>
                  <button type="button" className="btn-close" onClick={() => setEditOpen(false)} />
                </div>

                <div className="modal-body">
                  <div className="row g-3">
                    <div className="col-md-3">
                      <label className="form-label">Country</label>
                      <select className="form-control" name="countryId" value={editForm.countryId} onChange={handleEditChange}>
                        <option value="">Select</option>
                        {countries.map((c) => (
                          <option key={c._id} value={c._id}>
                            {c.name} ({c.iso2 || "-"})
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-md-3">
                      <label className="form-label">State (optional)</label>
                      <select className="form-control" name="stateId" value={editForm.stateId} onChange={handleEditChange} disabled={!editForm.countryId}>
                        <option value="">All</option>
                        {states.map((s) => (
                          <option key={s._id} value={s._id}>
                            {s.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-md-3">
                      <label className="form-label">City (optional)</label>
                      <select className="form-control" name="cityId" value={editForm.cityId} onChange={handleEditChange} disabled={!editForm.stateId}>
                        <option value="">All</option>
                        {cities.map((c) => (
                          <option key={c._id} value={c._id}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-md-3">
                      <label className="form-label">Area (optional)</label>
                      <select className="form-control" name="areaId" value={editForm.areaId} onChange={handleEditChange} disabled={!editForm.cityId}>
                        <option value="">All</option>
                        {editAreasOptions.map((a) => (
                          <option key={a._id} value={a._id}>
                            {a.name} {a.pincode ? `(${a.pincode})` : ""}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-md-3">
                      <label className="form-label">Category (optional)</label>
                      <select className="form-control" name="categoryId" value={editForm.categoryId} onChange={handleEditChange}>
                        <option value="">All Categories</option>
                        {categories.map((cat) => (
                          <option key={cat._id} value={cat._id}>
                            {cat.title}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-md-2">
                      <label className="form-label">Tax Type</label>
                      <select className="form-control" name="taxType" value={editForm.taxType} onChange={handleEditChange}>
                        {TAX_TYPE.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-md-2">
                      <label className="form-label">Rate (%)</label>
                      <input className="form-control" name="rate" value={editForm.rate} onChange={handleEditChange} />
                    </div>

                    <div className="col-md-2">
                      <label className="form-label">Inclusive</label>
                      <div className="form-check">
                        <input className="form-check-input" type="checkbox" name="isInclusive" checked={editForm.isInclusive} onChange={handleEditChange} />
                        <label className="form-check-label">Price includes tax</label>
                      </div>
                    </div>

                    <div className="col-md-2">
                      <label className="form-label">Effective From</label>
                      <input className="form-control" type="date" name="effectiveFrom" value={editForm.effectiveFrom} onChange={handleEditChange} />
                    </div>

                    <div className="col-md-2">
                      <label className="form-label">Effective To</label>
                      <input className="form-control" type="date" name="effectiveTo" value={editForm.effectiveTo} onChange={handleEditChange} />
                    </div>

                    <div className="col-md-2">
                      <label className="form-label">Status</label>
                      <div className="d-flex gap-3">
                        <div className="form-check">
                          <input className="form-check-input" type="radio" checked={editForm.status === true} onChange={() => setEditForm((p) => ({ ...p, status: true }))} />
                          <label className="form-check-label text-success">Active</label>
                        </div>
                        <div className="form-check">
                          <input className="form-check-input" type="radio" checked={editForm.status === false} onChange={() => setEditForm((p) => ({ ...p, status: false }))} />
                          <label className="form-check-label text-danger">Inactive</label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setEditOpen(false)} disabled={editLoading}>
                    Close
                  </button>
                  <button className="btn btn-primary" onClick={updateTaxRule} disabled={editLoading}>
                    {editLoading ? "Updating..." : "Update"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </>
  );
}